import React from "react";
import { Box, ButtonBase, Divider, Stack, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { MONO_FONT } from "./SetupCard";

/**
 * The sticky review panel beside the cards: the composed exploration ID, the
 * setup checklist (one row per card that gates Start; a row opens its card)
 * and the Start actions, which the start section renders into `actionsRef`'s
 * box.
 *
 * items: [{ key, label, value, complete, missing }]
 */
export default function ReviewPanel({ explorationName, items, onJump, actionsRef }) {
  const done = items.filter((item) => item.complete).length;

  return (
    <Box
      component="aside"
      aria-label="Review"
      sx={{
        position: { md: "sticky" },
        top: { md: 16 },
        borderRadius: 3,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        bgcolor: "#ffffff",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.25,
          pb: 2,
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          color: "#ffffff",
        }}
      >
        <Typography
          variant="overline"
          sx={{ display: "block", letterSpacing: 1.2, fontWeight: 700, lineHeight: 1.6, color: "rgba(255, 255, 255, 0.65)" }}
        >
          Exploration ID
        </Typography>
        <Typography
          data-testid="review-exploration-id"
          sx={{
            fontFamily: MONO_FONT,
            fontWeight: 600,
            fontSize: "0.88rem",
            lineHeight: 1.45,
            overflowWrap: "anywhere",
            color: explorationName ? "#ffffff" : "rgba(255, 255, 255, 0.55)",
          }}
        >
          {explorationName || "Composed once the setup is complete"}
        </Typography>
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="overline" sx={{ letterSpacing: 1.2, fontWeight: 700, color: "text.secondary" }}>
            Setup
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
            {`${done} of ${items.length} complete`}
          </Typography>
        </Stack>
      </Box>
      <Stack sx={{ px: 1.5, pb: 1.5 }} spacing={0.25}>
        {items.map((item) => (
          <ButtonBase
            key={item.key}
            onClick={() => onJump(item.key)}
            data-testid={`review-item-${item.key}`}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 1.25,
              width: "100%",
              textAlign: "left",
              px: 1,
              py: 0.75,
              borderRadius: 2,
              transition: "background-color 0.15s ease",
              "&:hover": { bgcolor: "rgba(15, 23, 42, 0.04)" },
            }}
          >
            {item.complete ? (
              <CheckCircleRoundedIcon sx={{ fontSize: 20, color: "#1b7f3b", flexShrink: 0 }} />
            ) : item.missing ? (
              <ErrorOutlineRoundedIcon sx={{ fontSize: 20, color: "#b45309", flexShrink: 0 }} />
            ) : (
              <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 20, color: "#94a3b8", flexShrink: 0 }} />
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" sx={{ display: "block", lineHeight: 1.2, color: "text.secondary" }}>
                {item.label}
              </Typography>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: 600, color: item.missing ? "#92400e" : "#0f172a" }}
              >
                {item.value}
              </Typography>
            </Box>
          </ButtonBase>
        ))}
      </Stack>

      <Divider />
      <Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>
        <Box ref={actionsRef} />
      </Box>
    </Box>
  );
}
